import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { useDataCoordinator } from "@/hooks/useDataCoordinator";
import { Button } from "@/components/ui/button";
import ShortlistDetail from "@/components/shortlists/ShortlistDetail";
import ShortlistCard from "@/components/shortlists/ShortlistCard";
import CreateShortlistDialog from "@/components/shortlists/CreateShortlistDialog";
import { ShortlistCardSkeleton } from "@/components/ui/SkeletonLoader";

export default function Shortlists() {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [location, navigate] = useLocation();
  
  // Use data coordinator for unified data access
  const { shortlists, isInitialLoading } = useDataCoordinator();
  
  // Parse shortlist ID from URL if present
  const shortlistId = location.includes('/shortlists/') 
    ? parseInt(location.split('/shortlists/')[1]) 
    : null;
  
  const selectedShortlist = shortlistId 
    ? shortlists.find(s => s.id === shortlistId) 
    : null;
  
  // Redirect to shortlists page if the shortlist doesn't exist
  useEffect(() => {
    if (shortlistId && !isInitialLoading && !selectedShortlist) {
      navigate("/shortlists");
    }
  }, [shortlistId, selectedShortlist, isInitialLoading, navigate]);
  
  if (isInitialLoading) {
    return (
      <div className="px-4 py-6 lg:px-6 xl:px-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-neutral-800">Shortlists</h1>
            <p className="text-sm text-neutral-500">Create and manage your player shortlists</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => (
            <ShortlistCardSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }
  
  return (
    <div className="px-4 py-6 lg:px-6 xl:px-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-800">Shortlists</h1>
          <p className="text-sm text-neutral-500">Create and manage your player shortlists</p>
        </div>
        
        <div className="mt-4 sm:mt-0">
          <Button onClick={() => setIsCreateDialogOpen(true)}>
            <i className="fas fa-plus mr-2"></i> Create Shortlist
          </Button>
        </div>
      </div>
      
      {/* Create Shortlist Dialog */}
      <CreateShortlistDialog 
        isOpen={isCreateDialogOpen} 
        onClose={() => setIsCreateDialogOpen(false)}
      />
      
      {/* Render shortlists or detailed view */}
      {selectedShortlist ? (
        <ShortlistDetail 
          shortlist={selectedShortlist} 
          onBack={() => navigate("/shortlists")} 
        />
      ) : (
        <>
          {shortlists.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-8 text-center">
              <div className="text-neutral-400 mb-4">
                <i className="fas fa-star text-4xl"></i>
              </div>
              <h3 className="text-lg font-medium text-neutral-900 mb-2">No shortlists yet</h3>
              <p className="text-neutral-600 mb-6">
                Create your first shortlist to start organizing your top player prospects.
              </p>
              <Button onClick={() => setIsCreateDialogOpen(true)}>
                <i className="fas fa-plus mr-2"></i> Create Shortlist
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {shortlists.map((shortlist) => (
                <ShortlistCard
                  key={shortlist.id}
                  shortlist={shortlist}
                  onClick={() => navigate(`/shortlists/${shortlist.id}`)}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
